import { ApolloError, NetworkStatus, useQuery } from '@apollo/client';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApollo } from 'config/apollo-old';
import { InternationalContext } from 'contexts/international';
import { InternationalFilterContext } from 'contexts/internationalFilter';
import { TripType } from 'contexts/searchParams';
import { loadFilterStore } from 'list/international/filter/filterStore';
import {
  IInternationalContextResult,
  IQueryValue,
  IReceivedInternationalResult,
} from 'list/international/types';
import {
  defaultInternationalResult,
  mergeInternationalList,
} from 'list/international/utils';
import INTERNATIONAL_LIST from './internationalList.old.gql';
import { IAirportDetail } from './useAirportDetailList';

const PULLING_INTERVAL = 2000; // 2sec

interface IData {
  internationalList: IReceivedInternationalResult;
}

export const defaultVariables: IQueryValue = {
  fareType: 'Y',
  isDirect: false,
  itinerary: [],
  adult: 1,
  child: 0,
  infant: 0,
  stayLength: '',
  trip: TripType.NULL,
  galileoFlag: true,
  galileoKey: '',
  travelBizFlag: true,
  travelBizKey: '',
};

const internationalInputKeys = [
  'fareType',
  'isDirect',
  'itinerary',
  'adult',
  'child',
  'infant',
  'stayLength',
  'trip',
  'where',
];

const getOpenTicketVariables = (queryVariables: IQueryValue) => {
  const { itinerary, stayLength, ...rest } = queryVariables;
  if (itinerary.length !== 2) {
    return queryVariables;
  }
  const [dep, arr] = itinerary;
  return {
    itinerary: [dep, { ...arr, departureDate: '' }],
    stayLength,
    ...rest,
  };
};

export default function useInternationalFlight(
  queryVariables: IQueryValue,
  itineraryIataDetails: IAirportDetail[],
): IInternationalContextResult {
  const { isDirect, itinerary, stayLength, trip } = queryVariables;
  const { orgFlights, expFlights, setFlightList } =
    useContext(InternationalContext);
  const { setFilterStore } = useContext(InternationalFilterContext);
  const startTime = useRef(Date.now());
  const timeoutId = useRef<number>();
  const [completed, setCompleted] = useState(false);
  const variables = stayLength
    ? getOpenTicketVariables(queryVariables)
    : queryVariables;
  const refinedVariables = JSON.stringify(
    internationalInputKeys.map(key => variables[key]),
  );
  const observedVariables = useMemo(() => variables, [refinedVariables]);
  const client = useApollo();
  const {
    data: prevResult = { internationalList: defaultInternationalResult },
    error,
    networkStatus,
    fetchMore,
  } = useQuery(INTERNATIONAL_LIST, {
    client,
    variables: observedVariables,
    ssr: false,
    onCompleted: onLoadMore(orgFlights),
    onError(error) {
      onLoadMore(orgFlights, error)(prevResult);
    },
    fetchPolicy: 'network-only',
  });
  const maxTimeout = (queryVariables.isDirect ? 13 : 20) * 1000;

  useEffect(() => {
    if (networkStatus === NetworkStatus.setVariables) {
      startTime.current = Date.now();
      if (timeoutId.current) window.clearTimeout(timeoutId.current);
      setCompleted(false);
      setFlightList();
    }
  }, [networkStatus]);

  function onLoadMore(
    orgFlights?: IReceivedInternationalResult,
    error?: ApolloError,
  ): (data: IData) => void {
    return ({
      internationalList,
      internationalList: {
        galileoFlag,
        galileoKey,
        travelBizFlag,
        travelBizKey,
      },
    }: IData = prevResult) => {
      if (timeoutId.current) window.clearTimeout(timeoutId.current);

      const newOrgFlights = mergeInternationalList(
        orgFlights,
        internationalList,
      );
      const newFilterStore = loadFilterStore(
        itinerary,
        trip,
        itineraryIataDetails,
        isDirect,
        stayLength,
        newOrgFlights?.results,
      );

      setFlightList(newOrgFlights, newFilterStore);

      const elapsedTime = Date.now() - startTime.current;

      if (
        error?.graphQLErrors.some(
          error => error.extensions.code === 'BAD_USER_INPUT',
        ) ||
        (error?.networkError as any)?.statusCode === 400 ||
        (!galileoFlag && !travelBizFlag) ||
        elapsedTime + PULLING_INTERVAL >= maxTimeout
      ) {
        setCompleted(true);
        setFilterStore(newFilterStore);
        return;
      }

      timeoutId.current = window.setTimeout(async () => {
        try {
          const { data: curResult } = await fetchMore({
            variables: {
              ...observedVariables,
              galileoFlag,
              galileoKey,
              travelBizFlag,
              travelBizKey,
            },
            updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult || prev,
          });
          return onLoadMore(newOrgFlights)(curResult);
        } catch (error) {
          return onLoadMore(
            newOrgFlights,
            error instanceof ApolloError ? error : undefined,
          )(prevResult);
        }
      }, PULLING_INTERVAL);
    };
  }

  const defaultResult = {
    completed,
    error,
    localErrors: [],
    fares: new Map(),
    itinerary,
    naverFarePromotion: {
      isAdultDiscount: false,
      adultDiscountPrice: 0,
      isChildDiscount: false,
      childDiscountPrice: 0,
    },
    resCnt: 0,
    totalResCnt: 1,
    schedules: [],
    schs: [],
    carbonEmissionAverage: {},
  };

  if (!orgFlights) return { ...defaultResult, error: 'noData' };

  const { resCnt, totalResCnt, results } = orgFlights;
  const { airlines, airports, errors, fareTypes, fares, schedules, schs } =
    expFlights || {
      airlines: {},
      airports: {},
      errors: [],
      fareTypes: {},
      fares: new Map(),
      schedules: [],
      schs: [],
    };
  return {
    ...defaultResult,
    airlines,
    airports,
    fareTypes,
    fares,
    localErrors: errors,
    resCnt: completed ? Math.max(totalResCnt, 1) : resCnt,
    totalResCnt: completed ? Math.max(totalResCnt, 1) : totalResCnt,
    schedules,
    schs,
    carbonEmissionAverage: results.carbonEmissionAverage,
  };
}
